<script>
import { required, email } from 'vuelidate/lib/validators';
import { prospectComputed, prospectMethods } from '@state/helpers';
import getbackgroundColor from '@utils/calculate-hsl-color-from-string';
import getInitials from '@utils/get-initials';
import isPhone from '@utils/phone-validation';
const EditableText = () => import('@components/editables/editable-text.vue');
export default {
  components: {
    EditableText,
  },
  props: {
    importLoader: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      expanded: false,
    };
  },
  validations: {
    contacts: {
      $each: {
        phone: {
          $each: {
            isPhone,
          },
        },
        email: {
          required,
          $each: {
            required,
            email,
          },
        },
        firstName: {
          required,
        },
        lastName: {
          required,
        },
        id: {
          required,
        },
        conflicts: {},
        selected: {},
      },
    },
  },
  watch: {
    contacts: {
      deep: true,
      handler() {
        let selectedProspects = 0;
        let validProspects = 0;

        for (const contact in this.$v.contacts.$each.$iter) {
          if (this.$v.contacts.$each.$iter[contact].$model.selected) {
            selectedProspects += 1;
            if (!this.$v.contacts.$each.$iter[contact].$invalid) {
              validProspects += 1;
            }
          }
        }
        this.$emit(
          'are-prospect-valid',
          selectedProspects > 0 && selectedProspects === validProspects
        );
      },
    },
  },
  computed: {
    ...prospectComputed,
    showEmptySlots() {
      // a max of 5 slots when no contacts at all!
      if (this.contacts && this.contacts.length < 5) {
        return 5 - this.contacts.length;
      } else {
        return 0;
      }
    },
  },
  methods: {
    ...prospectMethods,
    updateinfo(newValue, key, index) {
      if (newValue.includes(`click to add ${key}`)) {
        newValue = newValue.replace(`click to add ${key}`, '');
      }
      this.updateContact({ newValue, key, index });
    },
    makeUserAvatar(name) {
      return {
        initials: getInitials(name),
        background: getbackgroundColor({
          string: name,
          saturation: 50,
          lightness: 50,
        }),
      };
    },
    mostContactInfo(contact) {
      const most =
        contact.phone.$model.length > contact.email.$model.length
          ? contact.phone.$model
          : contact.email.$model;
      return most.slice(1);
    },
  },
};
</script>

<template>
  <table id="prospects">
    <thead>
      <tr>
        <th class="select-all" @click.stop="selectAllContacts">Select All</th>
        <th class="first">First Name</th>
        <th class="last">Last Name</th>
        <th class="phone">Phone Number</th>
        <th class="email">Email Address</th>
      </tr>
    </thead>
    <tbody>
      <template v-if="!emptyContacts">
        <template v-for="(v, index) in $v.contacts.$each.$iter">
          <tr :key="v.id.$model">
            <td class="avatar-cell">
              <div class="row">
                <div
                  :class="[
                    'avatar',
                    'initials',
                    { 'with-arrow': true, selected: v.selected.$model },
                  ]"
                  @click.stop="
                    setSelectedContacts({ selected: !v.selected.$model, index })
                  "
                >
                  <BaseIcon name="check-circle" v-show="v.selected.$model" />
                </div>
                <div
                  class="caret-icon"
                  @click.stop="expanded = !expanded"
                  v-if="Object.keys(v.phone.$each.$iter).length > 1"
                >
                  <BaseIcon name="caret-up" v-if="expanded" />
                  <BaseIcon name="caret-down" v-else />
                </div>
              </div>
            </td>
            <td :class="{ 'missing-info': !v.firstName.$model }">
              <p v-if="!v.firstName.$model"
                >first name required to add prospect</p
              >
              <EditableText
                v-model.lazy="v.firstName.$model"
                :text="v.firstName.$model || 'click to add first name'"
                :canEdit="true"
                @update-editable-value="updateinfo($event, 'firstName', index)"
              />
            </td>
            <td :class="{ 'missing-info': !v.lastName.$model }">
              <p v-if="!v.lastName.$model"
                >last name required to add prospect</p
              >
              <EditableText
                v-model.lazy="v.lastName.$model"
                :text="v.lastName.$model || 'click to add last name'"
                :canEdit="true"
                @update-editable-value="updateinfo($event, 'lastName', index)"
              />
            </td>
            <td
              :class="{
                'missing-info':
                  !v.phone.$each.$model &&
                  v.phone.$each.$iter[0] &&
                  !v.phone.$each.$iter[0].isPhone,
                deduped:
                  v.conflicts.$model &&
                  v.conflicts.$model.includes(
                    v.phone.$each.$iter[0] && v.phone.$each.$iter[0].$model
                  ),
              }"
            >
              <div class="info-w-icon">
                <div class="icon-wrapper">
                  <img src="@assets/images/phone.svg" />
                </div>
                <div class="w-100">
                  <p
                    v-if="
                      !v.phone.$each.$model &&
                        v.phone.$each.$iter[0] &&
                        !v.phone.$each.$iter[0].isPhone
                    "
                    >please enter valid phone
                  </p>
                  <p
                    v-else-if="
                      v.conflicts.$model &&
                        v.conflicts.$model.includes(
                          v.phone.$each.$iter[0] &&
                            v.phone.$each.$iter[0].$model
                        )
                    "
                    >phone is already taken
                  </p>
                  <EditableText
                    v-model.lazy="v.phone.$model[0]"
                    :text="
                      v.phone.$model[0] ||
                        (!v.phone.$model[0] ? 'click to add phone' : '')
                    "
                    :inputMask="v.phone.$model[0] ? '+1 (###) ###-####' : ''"
                    :canEdit="true"
                    @update-editable-value="updateinfo($event, 'phone', index)"
                  />
                </div>
              </div>
            </td>
            <td
              :class="{
                'missing-info':
                  (v.email.$each.$iter[0] &&
                    (!v.email.$each.$iter[0].required ||
                      v.email.$each.$iter[0].$invalid)) ||
                  !v.email.$model.length,
                deduped:
                  v.conflicts.$model &&
                  v.conflicts.$model.includes(
                    v.email.$each.$iter[0] && v.email.$each.$iter[0].$model
                  ),
              }"
            >
              <div class="info-w-icon">
                <div class="icon-wrapper">
                  <img src="@assets/images/envelope.svg" />
                </div>
                <div class="w-100">
                  <p
                    v-if="
                      (v.email && !v.email.required) ||
                        (v.email.$each.$iter[0] &&
                          !v.email.$each.$iter[0].required &&
                          v.email.$each.$iter[0].$invalid)
                    "
                    >email required to add prospect</p
                  >
                  <p
                    v-else-if="
                      v.email.$each.$iter[0] &&
                        !v.email.$each.$iter[0].email &&
                        v.email.$each.$iter[0].$invalid
                    "
                    >valid email required to add prospect</p
                  >
                  <p
                    v-else-if="
                      v.conflicts.$model &&
                        v.conflicts.$model.includes(
                          v.email.$each.$iter[0] &&
                            v.email.$each.$iter[0].$model
                        )
                    "
                    >email is already taken
                  </p>
                  <EditableText
                    v-model.lazy="v.email.$model[0]"
                    :text="
                      !v.email.$model[0]
                        ? 'click to add email'
                        : v.email.$model[0]
                    "
                    :canEdit="true"
                    @update-editable-value="updateinfo($event, 'email', index)"
                  />
                </div>
              </div>
            </td>
          </tr>
          <template
            v-if="
              (v.phone.$model.length > 1 || v.email.$model.length > 1) &&
                expanded
            "
          >
            <tr
              class="extra-contact-info"
              :key="`extra${v.id.$model}${i}`"
              v-for="(item, i) in mostContactInfo(v)"
            >
              <td></td>
              <td></td>
              <td></td>
              <td>
                <div class="info-w-icon">
                  <div class="icon-wrapper">
                    <img src="@assets/images/phone.svg" />
                  </div>
                  <span>
                    {{ v.phone.$model[i + 1] ? v.phone.$model[i + 1] : '' }}
                  </span>
                </div>
              </td>
              <td>
                <div class="info-w-icon">
                  <div class="icon-wrapper">
                    <img src="@assets/images/envelope.svg" />
                  </div>
                  <span>
                    {{ v.email.$model[i + 1] ? v.email.$model[i + 1] : '' }}
                  </span>
                </div>
              </td>
            </tr>
          </template>
        </template>
      </template>
      <template v-if="showEmptySlots">
        <tr class="empty" v-for="(n, i) in showEmptySlots" :key="i">
          <td><div class="avatar" :class="{ loading: importLoader }"></div></td>
          <td
            ><div
              class="block"
              :style="{ width: Math.random() * 50 + 50 + '%' }"
            ></div
          ></td>
          <td
            ><div
              class="block"
              :class="{ loading: importLoader }"
              :style="{ width: Math.random() * 50 + 50 + '%' }"
            ></div
          ></td>
          <td
            ><div
              class="block phone_block"
              :class="{ loading: importLoader }"
            ></div
          ></td>
          <td
            ><div
              class="block"
              :class="{ loading: importLoader }"
              :style="{ width: Math.random() * 50 + 50 + '%' }"
            ></div
          ></td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-max-type
// stylelint-disable selector-pseudo-element-no-unknown
// stylelint-disable no-descending-specificity
$table-border: 1px solid $color-cohort-gray;
@mixin display-flex {
  align-items: center;
  display: flex;
  justify-content: center;
}

@mixin avatar-dimensions {
  border-radius: 50%;
  height: 35px;
  width: 35px;
}

table {
  border: none;
  border-collapse: separate;
  border-spacing: 0;
  white-space: nowrap;
  width: 750px;
  tbody {
    border: none;

    ::v-deep .input-inline-edit {
      font-size: 1.5rem;
      font-weight: normal;
      line-height: 1.5rem;
      text-align: left;
      width: 100%;
    }

    .extra-contact-info {
      background: rgba($color-light-gray-alt-wo-rgb, 0.3);
      td {
        border-bottom: 4px solid rgba($color-black-wo-rgb, 0.2);
      }
    }
  }
  th {
    background-color: $color-white;
    border-bottom: $table-border;
    color: $color-black-one;
    font-size: 1.4rem;
    font-weight: bold;
    letter-spacing: -0.25px;
    position: sticky;
    top: 0;

    // adding up to 100%
    &.select-all {
      color: $color-action;
      cursor: pointer;
      width: 10%;
    }
    &.first,
    &.last {
      min-width: 150px;
      width: 17.5%;
    }
    &.phone {
      min-width: 220px;
      text-align: center;
      width: 25%;
    }
    &.email {
      min-width: 260px;
      width: 30%;
    }
  }
  td {
    font-size: 1.5rem;

    &.avatar-cell {
      .row {
        cursor: pointer;
        @include display-flex;
        .caret-icon {
          margin-left: 15px;
          &:hover {
            background: rgba($color-light-gray-alt-wo-rgb, 0.4);
            border-radius: 50%;
            height: 15px;
            width: 15px;
            @include display-flex;
          }
        }

        ::v-deep .fa-check-circle {
          cursor: pointer;
          display: block;
          margin-bottom: 0;
          @include avatar-dimensions;

          path {
            fill: $color-action;
          }
        }
      }
    }

    &.deduped {
      background: rgba($color-yellow-wo-rgb, 0.3);
      p,
      ::v-deep .inline-text {
        letter-spacing: -0.25px;
        line-height: 1;
        text-align: center;
      }
      p {
        font-size: 0.9rem;
        font-weight: 300;
        margin-bottom: 0;
        opacity: 0.5;
        text-transform: capitalize;
      }
      ::v-deep .inline-text {
        cursor: pointer;
        display: block;
        font-size: 1.1rem;
        font-weight: bold;
        padding-top: 5px;
        text-align: center;
      }
    }

    &.missing-info {
      background: rgba($color-danger-wo-rgb, 0.3);
      p,
      ::v-deep .inline-text {
        letter-spacing: -0.25px;
        line-height: 1;
        text-align: center;
        text-transform: capitalize;
      }
      p {
        color: $color-black-one;
        font-size: 0.8rem;
        font-weight: 300;
        margin-bottom: 0;
        opacity: 0.5;
      }
      ::v-deep .inline-text {
        color: $color-danger;
        cursor: pointer;
        display: block;
        font-size: 1.1rem;
        font-weight: bold;
        padding-top: 5px;
        text-align: center;
      }
    }

    .info-w-icon {
      align-items: center;
      display: flex;
      .icon-wrapper {
        background: rgba($color-light-gray-alt-wo-rgb, 0.2);
        height: 35px;
        margin-right: 10px;
        width: 40px;
        @include display-flex;
      }
    }
    .block {
      background: $color-off-white-two;
      height: 15px;
      width: 70%;
      &.loading {
        @extend .animated-background;
      }
      &.phone_block {
        margin: 0 auto;
      }
    }
    .avatar {
      background: $color-off-white-two;
      border-radius: 10%;
      height: 35px;
      margin: 0 auto;
      width: 35px;
      &.loading {
        @extend .animated-background;
      }
      &.initials {
        border: 1px solid blue;
        cursor: pointer;
        @include display-flex;

        &:hover {
          border-radius: 50%;
        }
        &::after {
          background: rgba($color-white-wo-rgb, 0.8);
          content: url(../../assets/images/icon-check-transparent.svg);
          font-size: 30px;
          opacity: 0;
          position: absolute;
          transition: all 0.5s;
          @include display-flex;
          @include avatar-dimensions;
        }

        &:hover::after {
          opacity: 1;
        }
      }

      &.with-arrow {
        margin: 0;
      }
      &.selected {
        background: $color-white;
        border-radius: 50%;
      }
    }
  }
  td,
  th {
    border-bottom: $table-border;
    padding: 0.75em;
  }
  td + td,
  th + th {
    border-left: $table-border;
  }
}
@keyframes placeHolderShimmer {
  0% {
    background-position: -250px 0;
  }
  100% {
    background-position: 250px 0;
  }
}
.animated-background {
  animation-duration: 1.25s;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
  animation-name: placeHolderShimmer;
  animation-timing-function: linear;
  background: linear-gradient(
    to right,
    $color-off-white-two 8%,
    $color-off-white-six 18%,
    $color-off-white-two 33%
  );
  background-size: 400px 15px;
  position: relative;
}
</style>
